<script lang="ts">
  /**
   * SetStrokeStrip — the set's own picture on its Sets-tab row (Scott,
   * 2026-09-11: "on the sets you should show what the set actually is visually
   * instead of having to click it to see the colors").
   *
   * The share story card's set-row treatment, brought onto the list: the
   * lengths in SWIM ORDER, each in its stroke's colour from the one palette
   * (designTokens.STROKE_COLORS). Which of the three shapes it draws — band,
   * tiles, bar — is setStrokeStrip.ts's call, not this file's; here it is only
   * pixels. A tile or band row fits the +12 px budget: 3 px above, a 16 px band
   * of colour, 3 px below, replacing the 10 px gap the card already had. A BAR
   * row adds its caption and runs to +20 px, the ceiling the orchestrator set
   * when the top-of-list legend was deleted (VP round 1, B1) — the legend's own
   * line is off the list, so the section is no taller than it was.
   *
   * aria-hidden: the card's own aria-label already says the set's name and
   * meta, and the set page reads every length out in words. A screen reader
   * hearing "Butterfly, Butterfly, Breaststroke…" per row would be worse than
   * silence.
   */
  import { strokeColor, contrastTextOn, strokeWord } from "../lib/designTokens";

  import type { SetStrokeStrip } from "../lib/setStrokeStrip";

  interface Props {
    /** Which shape to draw, decided by setStrokeStrip.strokeStripFor. */
    strip: SetStrokeStrip;
  }
  let { strip }: Props = $props();
</script>

{#if strip.kind === "band"}
  {@const fill = strokeColor(strip.stroke)}
  <div class="strip" aria-hidden="true">
    <span class="band" style:background={fill} style:color={contrastTextOn(fill)}>{strip.label}</span>
  </div>
{:else if strip.kind === "tiles"}
  <div class="strip" class:long={strip.caption !== null} aria-hidden="true">
    <span class="row">
      {#each strip.tiles as stroke, i (i)}
        {@const fill = strokeColor(stroke)}
        <span class="tile" style:background={fill} style:color={contrastTextOn(fill)}>
          {#if strip.labelled}{strokeWord(stroke)}{/if}
        </span>
      {/each}
    </span>
    {#if strip.caption !== null}<span class="caption">{strip.caption}</span>{/if}
  </div>
{:else if strip.kind === "bar"}
  <div class="strip long" aria-hidden="true">
    <span class="row bar">
      {#each strip.cells as stroke, i (i)}
        <span class="cell" style:background={strokeColor(stroke)}></span>
      {/each}
    </span>
    <span class="caption">{strip.caption}</span>
  </div>
{/if}

<style>
  /* The strip owns the gap on BOTH sides (the card zeroes the name's bottom
     margin and the tiles' top margin), so the row grows 3 + 16 + 3 − 10 = 12 px. */
  .strip {
    display: flex;
    align-items: center;
    gap: 3px;
    margin: 3px 0;
    height: 16px;
  }
  /* The lengths, left to right, in the order they were swum. */
  .row {
    display: flex;
    align-items: center;
    gap: 3px;
    flex: 1 1 auto;
    min-width: 0;
  }
  /* One tile per length, in swim order. */
  .tile {
    flex: 1 1 0;
    min-width: 0;
    height: 16px;
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
    font-family: var(--font-text);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 16px;
    text-align: center;
    overflow: hidden;
  }
  /* One stroke, N lengths: "20 × FREE" rather than a wall of identical tiles. */
  .band {
    flex: 1 1 auto;
    height: 16px;
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
    font-family: var(--font-text);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    line-height: 16px;
    text-align: center;
  }
  /* A wordless row stacks: the colour, then the caption that names it. */
  .strip.long {
    flex-direction: column;
    align-items: stretch;
    gap: 1px;
    height: auto;
    margin: 2px 0;
  }
  /* 12+ mixed lengths: the same row of lengths, smaller and wordless. The 2 px
     gutter between cells is the card's own ground — a 1 px hairline was in the
     pixels and invisible to 5 of 5 readers, who then counted the runs and read
     the bar as contradicting its caption (VP round 3). */
  .bar {
    gap: 2px;
    flex: 0 0 auto;
    height: 12px;
  }
  .cell {
    flex: 1 1 0;
    min-width: 2px;
    height: 12px;
    border-radius: 2px;
  }
  /* The bar's own key, travelling with its row (the list-top legend is gone). */
  .caption {
    font-family: var(--font-text);
    /* 12 px, the card's own small-text size (the dates, the "/ length" unit) —
       nothing on this card is smaller (low-vision sizing). */
    font-size: 12px;
    line-height: 13px;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
